import React from 'react';
import { Dispatch } from 'redux';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { LegalNonCriminalCourtActionsItemModel } from 'forms/sections/legal/court/types';
import {
  CommonScrollIntoView,
  CommonAddressBooks,
  CommonRequired,
  OnError,
  OnUpdate,
} from 'types/props';
import { isPVQForm } from 'helpers/form';
import Branch from 'components/Form/Branch';
import PVQName from 'components/Form/PVQName';
import PVQLocation from 'components/Form/PVQLocation';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Radio from 'components/Form/Radio';
import RadioGroup from 'components/Form/RadioGroup';
import { courtPartyStatus, civilCourtOutcomeOptions } from 'constants/enums/legalOptions';
import LocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import AdditionalOppositions from './AdditionalOppositions';

interface Props extends Partial<LegalNonCriminalCourtActionsItemModel>, ValidationElementProps {
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  onUpdate?: OnUpdate<LegalNonCriminalCourtActionsItemModel>
  onError?: OnError
  required?: CommonRequired
  dispatch?: Dispatch
}

export default class NonCriminalCourtAction extends ValidationElement<Props> {
  static defaultProps = {
    addressBooks: {},
    dispatch: (action) => { },
    onUpdate: (queue) => { },
    onError: (value, arr) => arr,
    CivilActionDate: {} as LegalNonCriminalCourtActionsItemModel['CivilActionDate'],
    CourtName: {} as LegalNonCriminalCourtActionsItemModel['CourtName'],
    CourtAddress: {} as LegalNonCriminalCourtActionsItemModel['CourtAddress'],
    NatureOfAction: {} as LegalNonCriminalCourtActionsItemModel['NatureOfAction'],
    ResultsOfAction: {} as LegalNonCriminalCourtActionsItemModel['ResultsOfAction'],
    PrincipalPartyNames: {} as LegalNonCriminalCourtActionsItemModel['PrincipalPartyNames'],
  };

  update = (queue: Partial<LegalNonCriminalCourtActionsItemModel>) => {
    this.props.onUpdate?.({
      CivilActionDate: this.props.CivilActionDate,
      CourtName: this.props.CourtName,
      CourtAddress: this.props.CourtAddress,
      NatureOfAction: this.props.NatureOfAction,
      ResultsOfAction: this.props.ResultsOfAction,
      PrincipalPartyNames: this.props.PrincipalPartyNames,
      ApplicantCourtPartyStatus: this.props.ApplicantCourtPartyStatus,
      OppositionPartyName: this.props.OppositionPartyName,
      OppositionCourtPartyStatus: this.props.OppositionCourtPartyStatus,
      AdditionalOppositions: this.props.AdditionalOppositions,
      IsCivilCourtActionClosed: this.props.IsCivilCourtActionClosed,
      OutcomeOfAction: this.props.OutcomeOfAction,
      OutcomeOtherExplanation: this.props.OutcomeOtherExplanation,
      CivilCourtActionClosedDate: this.props.CivilCourtActionClosedDate,
      ScheduledCourtDate: this.props.ScheduledCourtDate,
      IsCivilActionAPublicRecord: this.props.IsCivilActionAPublicRecord,
      ...queue,
    });
  };

  updateCivilActionDate = (values: LegalNonCriminalCourtActionsItemModel['CivilActionDate']) => {
    this.update({ CivilActionDate: values });
  };

  updateCourtName = (values: LegalNonCriminalCourtActionsItemModel['CourtName']) => {
    this.update({ CourtName: values });
  };

  updateCourtAddress = (values: LegalNonCriminalCourtActionsItemModel['CourtAddress']) => {
    this.update({ CourtAddress: values });
  };

  updateNatureOfAction = (values: LegalNonCriminalCourtActionsItemModel['NatureOfAction']) => {
    this.update({ NatureOfAction: values });
  };

  updateResultsOfAction = (values: LegalNonCriminalCourtActionsItemModel['ResultsOfAction']) => {
    this.update({ ResultsOfAction: values });
  };

  updatePrincipalPartyNames = (
    values: LegalNonCriminalCourtActionsItemModel['PrincipalPartyNames']
  ) => {
    this.update({ PrincipalPartyNames: values });
  };

  updateApplicantCourtPartyStatus = (
    values: LegalNonCriminalCourtActionsItemModel['ApplicantCourtPartyStatus']
  ) => {
    this.update({ ApplicantCourtPartyStatus: values });
  };

  updateOppositionPartyName = (
    values: LegalNonCriminalCourtActionsItemModel['OppositionPartyName']
  ) => {
    this.update({ OppositionPartyName: values });
  };

  updateOppositionCourtPartyStatus = (
    values: LegalNonCriminalCourtActionsItemModel['OppositionCourtPartyStatus']
  ) => {
    this.update({ OppositionCourtPartyStatus: values });
  };

  updateAdditionalOppositions = (
    values: LegalNonCriminalCourtActionsItemModel['AdditionalOppositions']
  ) => {
    this.update({ AdditionalOppositions: values });
  };

  updateIsCivilCourtActionClosed = (
    values: LegalNonCriminalCourtActionsItemModel['IsCivilCourtActionClosed']
  ) => {
    if (!values?.value) {
      this.update({
        IsCivilCourtActionClosed: values,
        CivilCourtActionClosedDate: {} as LegalNonCriminalCourtActionsItemModel['CivilCourtActionClosedDate'],
        OutcomeOfAction: {} as LegalNonCriminalCourtActionsItemModel['OutcomeOfAction'],
        OutcomeOtherExplanation: {} as LegalNonCriminalCourtActionsItemModel['OutcomeOtherExplanation'],
        ScheduledCourtDate: {} as LegalNonCriminalCourtActionsItemModel['ScheduledCourtDate'],
      });
    }

    if (values?.value === 'No') {
      this.update({
        IsCivilCourtActionClosed: values,
        CivilCourtActionClosedDate: {} as LegalNonCriminalCourtActionsItemModel['CivilCourtActionClosedDate'],
        OutcomeOfAction: {} as LegalNonCriminalCourtActionsItemModel['OutcomeOfAction'],
        OutcomeOtherExplanation: {} as LegalNonCriminalCourtActionsItemModel['OutcomeOtherExplanation'],
      });
    }

    if (values?.value === 'Yes') {
      this.update({
        IsCivilCourtActionClosed: values,
        ScheduledCourtDate: {} as LegalNonCriminalCourtActionsItemModel['ScheduledCourtDate'],
      });
    }
  };

  updateOutcomeOfAction = (
    values: LegalNonCriminalCourtActionsItemModel['OutcomeOfAction']

  ) => {
    const outcomeOfAction = values?.value;
    const selected = [...((this.props.OutcomeOfAction || {}).values || [])];
    if (selected.includes(outcomeOfAction)) {
      selected.splice(selected.indexOf(outcomeOfAction), 1);
    } else {
      selected.push(outcomeOfAction);
    }
    if (selected.includes('Other')) {
      this.update({
        OutcomeOfAction: { values: selected },
      });
    } else {
      // if "Other" is deselected, reset 'OutcomeOtherExplanation' to an empty string
      this.update({
        OutcomeOtherExplanation: { name: '', value: '' },
        OutcomeOfAction: { values: selected }
      });
    }
  };

  updateOutcomeOtherExplanation = (
    values: LegalNonCriminalCourtActionsItemModel['OutcomeOtherExplanation']
  ) => {
    this.update({ OutcomeOtherExplanation: values });
  };

  updateCivilCourtActionClosedDate = (
    values: LegalNonCriminalCourtActionsItemModel['CivilCourtActionClosedDate']
  ) => {
    this.update({ CivilCourtActionClosedDate: values });
  };

  updateScheduledCourtDate = (
    values: LegalNonCriminalCourtActionsItemModel['ScheduledCourtDate']
  ) => {
    this.update({ ScheduledCourtDate: values });
  };

  updateIsCivilActionAPublicRecord = (
    values: LegalNonCriminalCourtActionsItemModel['IsCivilActionAPublicRecord']
  ) => {
    this.update({ IsCivilActionAPublicRecord: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className="non-criminal-court-action">
        <Field

          attribute={`${attribute}.ApplicantCourtPartyStatus`}
          title={i18n.t('legal.pvq.nonCriminalAction.heading.applicantCourtPartyStatus')}
          scrollIntoView={this.props.scrollIntoView}
          className="status"
        >
          <RadioGroup
            attribute={`${attribute}.ApplicantCourtPartyStatus`}
            className="applicantCourtPartyStatus option-list"
            selectedValue={(this.props.ApplicantCourtPartyStatus || {}).value}
            onError={this.handleError}
            required={this.props.required}
          >
            <Radio
              name="applicantCourtPartyStatus"
              className="applicantCourtPartyStatus yes"
              label={courtPartyStatus.plaintiff}
              value="Plaintiff"
              onUpdate={this.updateApplicantCourtPartyStatus}
              onError={this.props.onError}
            />
            <Radio
              name="applicantCourtPartyStatus"
              className="applicantCourtPartyStatus no"
              label={courtPartyStatus.defendant}
              value="Defendant"
              onUpdate={this.updateApplicantCourtPartyStatus}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <PVQName
          {...this.props.OppositionPartyName}
          attribute={`${attribute}.OppositionPartyName`}
          name="name"
          title={i18n.t('legal.pvq.nonCriminalAction.heading.oppositionPartyName')}
          aria-label="Provide your full name"
          showLastFirstMiddleNameOrder
          onUpdate={this.updateOppositionPartyName}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          textPrefix="applicantName"
          prefix="name"
        />
        <br />
        <br />

        <Field

          attribute={`${attribute}.OppositionCourtPartyStatus`}
          title={i18n.t('legal.pvq.nonCriminalAction.heading.oppositionCourtPartyStatus')}
          scrollIntoView={this.props.scrollIntoView}
          className="status"
        >
          <RadioGroup
            attribute={`${attribute}.OppositionCourtPartyStatus`}
            className="oppositionCourtPartyStatus option-list"
            selectedValue={(this.props.OppositionCourtPartyStatus || {}).value}
            onError={this.handleError}
            required={this.props.required}
          >
            <Radio
              name="oppositionCourtPartyStatus"
              className="oppositionCourtPartyStatus yes"
              label={courtPartyStatus.plaintiff}
              value="Plaintiff"
              onUpdate={this.updateOppositionCourtPartyStatus}
              onError={this.props.onError}
            />
            <Radio
              name="oppositionCourtPartyStatus"
              className="oppositionCourtPartyStatus no"
              label={courtPartyStatus.defendant}
              value="Defendant"
              onUpdate={this.updateOppositionCourtPartyStatus}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <AdditionalOppositions
          {...this.props.AdditionalOppositions}
          attribute={`${attribute}.AdditionalOppositions`}
          onUpdate={this.updateAdditionalOppositions}
          onError={this.props.onError}
          scrollIntoView
        />

        <Field
          title={i18n.t('legal.pvq.nonCriminalAction.heading.courtName')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <Text
            {...this.props.CourtName}
            attribute={`${attribute}.CourtName`}
            name="CourtName"
            className="court-name"
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtAddress`}
          exactAttribute
        >

          <LocationUnknown
            {...this.props.CourtAddress}
            label={i18n.t('legal.pvq.nonCriminalAction.heading.isCourtInUS')}
            domesticTitle={i18n.t('legal.pvq.nonCriminalAction.heading.domesticTitle')}
            foreignTitle={i18n.t('legal.pvq.nonCriminalAction.heading.foreignTitle')}
            attribute={`${attribute}.CourtAddress`}
            name="CourtAddress"
            className="court-address"
            customLayout={{ domesticFields: ['street', 'city', 'stateZipcode'], internationalFields: ['city', 'country'] }}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Court"
            dispatch={this.props.dispatch}
            onUpdate={this.updateCourtAddress}
            onError={this.props.onError}
            required={this.props.required}
            exactAttribute
          />
        </Field>

        <Field
          title={i18n.t('legal.pvq.nonCriminalAction.heading.natureOfAction')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.NatureOfAction`}
        >
          <Textarea
            {...this.props.NatureOfAction}
            attribute={`${attribute}.NatureOfAction`}
            name="NatureOfAction"
            className="nature-of-action"
            onUpdate={this.updateNatureOfAction}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field>
          <Branch
            {...this.props.IsCivilCourtActionClosed}
            attribute={`${attribute}.IsCivilCourtActionClosed`}
            name="IsCivilCourtActionClosed"
            label={i18n.t('legal.pvq.nonCriminalAction.heading.isCivilCourtActionClosed')}
            labelSize="h4"
            onUpdate={this.updateIsCivilCourtActionClosed}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
          {this.props?.IsCivilCourtActionClosed?.value === 'Yes' && (

            <>

              <Field
                title={i18n.t('legal.pvq.nonCriminalAction.heading.civilCourtActionClosedDate')}
                help="legal.nonCriminalAction.help.civilActionDate"
                adjustFor="datecontrol"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.CivilCourtActionClosedDate`}
              >
                <DateControl
                  {...this.props.CivilCourtActionClosedDate}
                  attribute={`${attribute}.CivilCourtActionClosedDate`}
                  name="CivilCourtActionClosedDate"
                  className="civil-action-closed-date"
                  minDateEqualTo
                  onUpdate={this.updateCivilCourtActionClosedDate}
                  onError={this.props.onError}
                  required={this.props.required}
                  hideDay
                />
              </Field>

              <Field
                title={i18n.t('legal.pvq.nonCriminalAction.heading.outcomeOfAction')}
                adjustFor="p"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.OutcomeOfAction`}
              >

                <CheckboxGroup
                  className="CourtOucomeOptions option-list option-list-vertical"
                  onError={this.props.onError}
                  required={this.props.required}
                  selectedValues={(this.props.OutcomeOfAction || {}).values}
                  attribute={`${attribute}.OutcomeOfAction`}
                >
                  {civilCourtOutcomeOptions.map((checkboxoption) => (
                    <Checkbox
                      name="CourtOucomeOptions"
                      label={checkboxoption}
                      value={checkboxoption}
                      className="awarded damages"
                      onUpdate={this.updateOutcomeOfAction}
                      onError={this.props.onError}
                    />
                  ))}

                </CheckboxGroup>
              </Field>
              {this.props.OutcomeOfAction?.values?.includes('Other') && (
                <Field
                  title={i18n.t('legal.pvq.nonCriminalAction.heading.otherCourtOutcomeExplanation')}
                  attribute={`${attribute}.OutcomeOtherExplanation`}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Textarea
                    {...this.props.OutcomeOtherExplanation}
                    name="OutcomeOtherExplanation"
                    attribute={`${attribute}.OutcomeOtherExplanation`}
                    className="outcome-other-explanation"
                    onUpdate={this.updateOutcomeOtherExplanation}
                    onError={this.props.onError}
                    required={this.props.required}
                  />
                </Field>

              )}
            </>
          )}
          {this.props?.IsCivilCourtActionClosed?.value === 'No' && (
            <Field
              title={i18n.t('legal.pvq.nonCriminalAction.heading.scheduledCourtDate')}
              help="legal.nonCriminalAction.help.civilActionDate"
              adjustFor="datecontrol"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ScheduledCourtDate`}
            >
              <DateControl
                {...this.props.ScheduledCourtDate}
                attribute={`${attribute}.ScheduledCourtDate`}
                name="CivilActionDate"
                className="civil-action-date"
                minDateEqualTo
                onUpdate={this.updateScheduledCourtDate}
                onError={this.props.onError}
                required={this.props.required}
                hideDay
              />
            </Field>
          )}
        </Field>

        <Branch
          {...this.props.IsCivilActionAPublicRecord}
          attribute={`${attribute}.IsCivilActionAPublicRecord`}
          name="IsCivilActionAPublicRecord"
          label={i18n.t('legal.pvq.nonCriminalAction.heading.isCivilActionAPublicRecord')}
          labelSize="h4"
          onUpdate={this.updateIsCivilActionAPublicRecord}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          showDNK
        />

      </div>
    ) : (
      <div className="non-criminal-court-action">

        <Field
          title={i18n.t('legal.nonCriminalAction.heading.civilActionDate')}
          help="legal.nonCriminalAction.help.civilActionDate"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CivilActionDate`}
        >
          <DateControl
            attribute={`${attribute}.CivilActionDate`}
            name="CivilActionDate"
            className="civil-action-date"
            minDateEqualTo
            {...this.props.CivilActionDate}
            onUpdate={this.updateCivilActionDate}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
          />
        </Field>

        <Field
          title={i18n.t('legal.nonCriminalAction.heading.courtName')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <Text
            attribute={`${attribute}.CourtName`}
            name="CourtName"
            className="court-name"
            {...this.props.CourtName}
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('legal.nonCriminalAction.heading.courtAddress')}
          optional
          adjustFor="address"
          help="legal.nonCriminalAction.help.courtAddress"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtAddress`}
        >
          <Location
            attribute={`${attribute}.CourtAddress`}
            name="CourtAddress"
            className="court-address"
            {...this.props.CourtAddress}
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Court"
            showPostOffice
            dispatch={this.props.dispatch}
            onUpdate={this.updateCourtAddress}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('legal.nonCriminalAction.heading.natureOfAction')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.NatureOfAction`}
        >
          <Textarea
            attribute={`${attribute}.NatureOfAction`}
            name="NatureOfAction"
            className="nature-of-action"
            {...this.props.NatureOfAction}
            onUpdate={this.updateNatureOfAction}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('legal.nonCriminalAction.heading.resultsOfAction')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ResultsOfAction`}
        >
          <Textarea
            attribute={`${attribute}.ResultsOfAction`}
            name="ResultsOfAction"
            className="results-of-action"
            {...this.props.ResultsOfAction}
            onUpdate={this.updateResultsOfAction}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('legal.nonCriminalAction.heading.principalPartyNames')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PrincipalPartyNames`}
        >
          <Textarea
            attribute={`${attribute}.PrincipalPartyNames`}
            name="PrincipalPartyNames"
            className="principal-party-names"
            {...this.props.PrincipalPartyNames}
            onUpdate={this.updatePrincipalPartyNames}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

      </div>
    );
  }
}
